import { X } from 'lucide-react';
import { useBlockedUsersInfinite } from '../../hooks/useBlockedUsersQuery';
import { useToast } from '../ui/Toast';
import { Skeleton } from '../ui/Skeleton';
import { useUnblockUserMutation } from '../../hooks/useBlockUser';

interface BlockedUsersModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function BlockedUserRowSkeleton() {
  return (
    <div className="flex items-center justify-between p-4 bg-[#2A2F36] rounded-lg">
      <div className="flex items-center gap-3">
        <Skeleton className="w-10 h-10 rounded-full" />
        <div className="space-y-2">
          <Skeleton className="h-4 w-36" />
          <Skeleton className="h-3 w-24" />
        </div>
      </div>
      <Skeleton className="h-10 w-20 rounded-lg" />
    </div>
  );
}

export function BlockedUsersModal({ isOpen, onClose }: BlockedUsersModalProps) {
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useBlockedUsersInfinite();
  const { toast } = useToast();

  const unblockMutation = useUnblockUserMutation();

  if (!isOpen) return null;

  const allBlockedUsers = data?.pages.flatMap((page) => page.blocked_users) || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="bg-[#1A1F26] rounded-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/[0.06]">
          <h2 className="text-xl font-semibold text-white">Blocked Users</h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <BlockedUserRowSkeleton key={`blocked-user-skeleton-${i}`} />
              ))}
            </div>
          ) : allBlockedUsers.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-400">No blocked users</p>
            </div>
          ) : (
            <div className="space-y-3">
              {allBlockedUsers.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-4 bg-[#2A2F36] rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    {user.avatar_url ? (
                      <img
                        src={user.avatar_url}
                        alt={user.display_name || user.username || 'User'}
                        className="w-10 h-10 rounded-full"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-slate-600 flex items-center justify-center text-white font-semibold">
                        {(user.display_name || user.username || 'U')[0].toUpperCase()}
                      </div>
                    )}
                    <div>
                      <div className="text-white font-medium">
                        {user.display_name || user.username || 'Unknown User'}
                      </div>
                      {user.username && (
                        <div className="text-sm text-slate-400">@{user.username}</div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => unblockMutation.mutate(user.id, {
                      onSuccess: () => toast.success('User unblocked'),
                      onError: () => toast.error('Failed to unblock user'),
                    })}
                    disabled={unblockMutation.isPending}
                    className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors font-medium disabled:opacity-50"
                  >
                    Unblock
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Load More */}
          {hasNextPage && (
            isFetchingNextPage ? (
              <div className="mt-3 space-y-3">
                {Array.from({ length: 2 }).map((_, i) => (
                  <BlockedUserRowSkeleton key={`blocked-user-next-page-skeleton-${i}`} />
                ))}
              </div>
            ) : (
              <div className="mt-6 text-center">
                <button
                  onClick={() => fetchNextPage()}
                  className="px-4 py-2 bg-[#2A2F36] hover:bg-[#3A3F46] text-white rounded-lg transition-colors font-medium"
                >
                  Load More
                </button>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
